import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  useTheme,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Tooltip,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Today as TodayIcon,
  Event as EventIcon,
  Restaurant as RestaurantIcon,
  Flight as FlightIcon,
  Groups as GroupsIcon,
  MedicalServices as MedicalServicesIcon,
  Person as PersonIcon,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import axios from 'axios';
import 'dayjs/locale/es';

const AgendaMedica = () => {
  const theme = useTheme();
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [events, setEvents] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    fecha: dayjs().format('YYYY-MM-DD'),
    evento: '',
    tipo: 'general',
    descripcion: '',
    hora_inicio: '09:00',
    hora_fin: '10:00',
    color: '#1976d2',
    recordatorio: false
  });

  const API_URL = process.env.REACT_APP_API_URL;
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  
  const weekdays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  
  const tipoColors = {
    viaje: '#4caf50',
    conferencia: '#2196f3',
    cena: '#ff9800',
    consulta: '#f44336',
    reunion: '#9c27b0',
    personal: '#00bcd4',
    general: '#607d8b'
  };

  const tipoIcons = {
    viaje: <FlightIcon fontSize="small" />,
    conferencia: <GroupsIcon fontSize="small" />,
    cena: <RestaurantIcon fontSize="small" />,
    consulta: <MedicalServicesIcon fontSize="small" />,
    reunion: <GroupsIcon fontSize="small" />,
    personal: <PersonIcon fontSize="small" />,
    general: <EventIcon fontSize="small" />
  };

  const tipoLabels = {
    viaje: 'Viaje',
    conferencia: 'Conferencia',
    cena: 'Cena',
    consulta: 'Consulta',
    reunion: 'Reunión',
    personal: 'Personal',
    general: 'General'
  };

  // Obtener eventos del mes actual
  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const mes = currentDate.month() + 1;
      const anio = currentDate.year();
      
      const response = await axios.get(`${API_URL}/api/agenda-medica/mes`, {
        params: { mes, anio }
      });
      
      // Organizar eventos por día
      const eventsByDay = {};
      response.data.forEach(event => {
        const day = dayjs(event.fecha).date();
        if (!eventsByDay[day]) {
          eventsByDay[day] = [];
        }
        eventsByDay[day].push(event);
      });
      
      setEvents(eventsByDay);
      setError('');
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Error al cargar la agenda. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  }, [currentDate, API_URL]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Navegación de meses
  const handlePrevMonth = () => {
    setCurrentDate(currentDate.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentDate(currentDate.add(1, 'month'));
  };

  const handleToday = () => {
    setCurrentDate(dayjs());
  };

  // Calcular días del mes
  const getCalendarDays = () => {
    const startOfMonth = currentDate.startOf('month');
    const daysInMonth = currentDate.daysInMonth();
    
    const days = [];
    
    // Obtener el primer día de la semana (0 = domingo, 1 = lunes, etc.)
    const firstDayOfWeek = startOfMonth.day();
    
    // Agregar días vacíos al inicio si el mes no comienza en domingo
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push({
        day: null,
        date: null,
        isCurrentMonth: false,
        isToday: false
      });
    }
    
    // Agregar días del mes actual
    for (let i = 1; i <= daysInMonth; i++) {
      const date = currentDate.set('date', i);
      const isToday = date.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD');
      
      days.push({
        day: i,
        date: date.format('YYYY-MM-DD'),
        isCurrentMonth: true,
        isToday
      });
    }
    
    // Calcular cuántos días vacíos agregar al final para completar 6 semanas (42 días)
    const totalCells = 42; // 6 semanas * 7 días
    const remainingCells = totalCells - days.length;
    
    for (let i = 0; i < remainingCells; i++) {
      days.push({
        day: null,
        date: null,
        isCurrentMonth: false,
        isToday: false
      });
    }
    
    return days;
  };

  // Manejar clic en día
  const handleDayClick = (day) => {
    if (!day || !day.date) return;
    
    setFormData({
      ...formData,
      fecha: day.date,
      evento: '',
      tipo: 'general',
      descripcion: '',
      hora_inicio: '09:00',
      hora_fin: '10:00',
      color: tipoColors.general
    });
    setEditingEvent(null);
    setOpenDialog(true);
  };

  // Manejar clic en evento
  const handleEventClick = (event, e) => {
    e.stopPropagation();
    setFormData({
      fecha: event.fecha,
      evento: event.evento,
      tipo: event.tipo,
      descripcion: event.descripcion || '',
      hora_inicio: event.hora_inicio || '09:00',
      hora_fin: event.hora_fin || '10:00',
      color: event.color || tipoColors[event.tipo],
      recordatorio: event.recordatorio || false
    });
    setEditingEvent(event);
    setOpenDialog(true);
  };

  // Guardar evento
  const handleSaveEvent = async () => {
    try {
      if (!formData.evento.trim()) {
        setError('El evento es requerido');
        return;
      }

      if (editingEvent) {
        await axios.put(`${API_URL}/api/agenda-medica/${editingEvent.id}`, formData);
      } else {
        await axios.post(`${API_URL}/api/agenda-medica`, formData);
      }
      
      setOpenDialog(false);
      fetchEvents();
    } catch (err) {
      console.error('Error saving event:', err);
      setError('Error al guardar el evento. Por favor, intente nuevamente.');
    }
  };

  // Eliminar evento
  const handleDeleteEvent = async (eventId, e) => {
    e.stopPropagation();
    if (window.confirm('¿Está seguro de eliminar este evento?')) {
      try {
        await axios.delete(`${API_URL}/api/agenda-medica/${eventId}`);
        fetchEvents();
      } catch (err) {
        console.error('Error deleting event:', err);
        setError('Error al eliminar el evento. Por favor, intente nuevamente.');
      }
    }
  };

  // Renderizar día del calendario
  const renderDay = (dayObj, index) => {
    const dayEvents = dayObj.day ? events[dayObj.day] || [] : [];
    
    return (
      <Paper
        key={index}
        elevation={0}
        sx={{
          minHeight: 75,
          height: '100%',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 0,
          bgcolor: dayObj.isCurrentMonth ? 'background.paper' : 'grey.50',
          position: 'relative',
          p: 0.4,
          display: 'flex',
          flexDirection: 'column',
          cursor: dayObj.isCurrentMonth ? 'pointer' : 'default',
          '&:hover': dayObj.isCurrentMonth ? {
            bgcolor: 'action.hover',
            '& .add-event-btn': {
              opacity: 1
            }
          } : {},
          transition: 'background-color 0.2s',
          borderLeft: index % 7 === 0 ? '1px solid' : 'none',
          borderLeftColor: 'divider'
        }}
        onClick={() => handleDayClick(dayObj)}
      >
        {/* Encabezado del día */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: dayObj.isToday ? 'bold' : 'normal',
              color: dayObj.isToday ? 'white' : 
                     dayObj.isCurrentMonth ? 'text.primary' : 'text.disabled',
              bgcolor: dayObj.isToday ? 'primary.main' : 'transparent',
              borderRadius: '50%',
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.65rem',
              lineHeight: 1
            }}
          >
            {dayObj.day || ''}
          </Typography>
          
          {/* Botón para agregar evento rápido */}
          {dayObj.isCurrentMonth && (
            <Tooltip title="Agregar evento">
              <IconButton
                size="small"
                className="add-event-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setFormData({
                    ...formData,
                    fecha: dayObj.date,
                    evento: '',
                    tipo: 'general',
                    descripcion: '',
                    hora_inicio: '09:00',
                    hora_fin: '10:00',
                    color: tipoColors.general
                  });
                  setEditingEvent(null);
                  setOpenDialog(true);
                }}
                sx={{ 
                  p: 0.25, 
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.light',
                    color: 'white'
                  }
                }}
              >
                <AddIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        
        {/* Eventos del día */}
        {dayObj.isCurrentMonth && (
          <Box sx={{ 
            flexGrow: 1, 
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '4px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '2px'
            }
          }}>
            {dayEvents.slice(0, 4).map((event, idx) => (
              <Tooltip key={event.id} title={`${event.hora_inicio || ''} ${event.evento}`}>
                <Box
                  sx={{
                    bgcolor: event.color || tipoColors[event.tipo],
                    color: 'white',
                    borderRadius: 0.25,
                    p: 0.2,
                    mb: 0.2,
                    cursor: 'pointer',
                    fontSize: '0.6rem',
                    '&:hover': {
                      opacity: 0.9
                    }
                  }}
                  onClick={(e) => handleEventClick(event, e)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1 }}>
                    <Typography variant="caption" sx={{ 
                      fontWeight: 'medium',
                      fontSize: '0.6rem',
                      lineHeight: 1,
                      flexGrow: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {event.evento.substring(0, 12)}
                    </Typography>
                  </Box>
                </Box>
              </Tooltip>
            ))}
            
            {dayEvents.length > 4 && (
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  display: 'block', 
                  textAlign: 'center',
                  mt: 0.5
                }}
              >
                +{dayEvents.length - 4} más
              </Typography>
            )}
            
            {dayEvents.length === 0 && (
              <Typography 
                variant="caption" 
                color="text.disabled"
                sx={{ 
                  display: 'block', 
                  textAlign: 'center',
                  fontStyle: 'italic',
                  mt: 2
                }}
              >
                Sin eventos
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    );
  };

  // Renderizar calendario mejorado
  const renderCalendar = () => {
    const days = getCalendarDays();
    
    return (
      <Box sx={{ 
        mt: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: 1,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '60vh'
      }}>
        {/* Encabezado de días de la semana */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'grey.900',
          borderBottom: '2px solid',
          borderColor: 'divider',
          flexShrink: 0
        }}>
          {weekdays.map((day, index) => (
            <Box
              key={day}
              sx={{
                p: 1.5,
                textAlign: 'center',
                fontWeight: 'bold',
                color: index === 0 ? 'error.main' : 
                       index === 6 ? 'primary.main' : 'text.primary',
                borderRight: '1px solid',
                borderRightColor: 'divider',
                '&:last-child': {
                  borderRight: 'none'
                }
              }}
            >
              {day}
            </Box>
          ))}
        </Box>

        {/* Días del mes */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gridAutoRows: 'minmax(75px, 1fr)',
          flex: 1,
          overflow: 'hidden'
        }}>
          {days.map((dayObj, index) => renderDay(dayObj, index))}
        </Box>
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
      }}>
        {/* Header flotante */}
        <Paper
          elevation={3}
          sx={{
            position: 'fixed',
            top: 64,
            left: 0,
            right: 0,
            zIndex: 1000,
            borderRadius: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: 3,
            py: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            color: 'white'
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'white', color: theme.palette.primary.main }}>
                <EventIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Agenda Médica
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Planificador de actividades profesionales
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<TodayIcon />}
                onClick={handleToday}
                sx={{ 
                  color: 'white', 
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                Hoy
              </Button>
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                bgcolor: 'rgba(255,255,255,0.1)',
                borderRadius: 2,
                px: 1,
                py: 0.5
              }}>
                <IconButton 
                  onClick={handlePrevMonth}
                  size="small"
                  sx={{ color: 'white' }}
                >
                  <ChevronLeft />
                </IconButton>
                
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold', 
                  minWidth: 200, 
                  textAlign: 'center',
                  px: 2
                }}>
                  {monthNames[currentDate.month()]} {currentDate.year()}
                </Typography>
                
                <IconButton 
                  onClick={handleNextMonth}
                  size="small"
                  sx={{ color: 'white' }}
                >
                  <ChevronRight />
                </IconButton>
              </Box>
              
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setFormData({
                    fecha: dayjs().format('YYYY-MM-DD'),
                    evento: '',
                    tipo: 'general',
                    descripcion: '',
                    hora_inicio: '09:00',
                    hora_fin: '10:00',
                    color: tipoColors.general,
                    recordatorio: false
                  });
                  setEditingEvent(null);
                  setOpenDialog(true);
                }}
                sx={{ 
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: 'grey.100'
                  }
                }}
              >
                Nuevo Evento
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Contenido principal - AHORA CON SCROLL */}
        <Box sx={{ 
          mt: '120px',
          px: { xs: 1, sm: 2, md: 3 },
          pb: 3,
          maxWidth: '1400px',
          mx: 'auto',
          width: '100%',
          boxSizing: 'border-box',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2 }} 
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {/* Calendario - AHORA CON SCROLL PROPIO */}
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              flex: 1
            }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}>
              {renderCalendar()}
            </Box>
          )}

          {/* Panel lateral con leyenda y resumen - AHORA DEBAJO DEL CALENDARIO */}
          <Box sx={{ display: 'none' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                {/* Estadísticas */}
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Resumen del Mes
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(tipoLabels).map(([tipo, label]) => {
                      const count = Object.values(events).flat().filter(e => e.tipo === tipo).length;
                      return (
                        <Grid item xs={6} sm={4} key={tipo}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: 'grey.50'
                          }}>
                            <Box sx={{ 
                              width: 40, 
                              height: 40, 
                              borderRadius: '50%',
                              bgcolor: tipoColors[tipo],
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white'
                            }}>
                              {React.cloneElement(tipoIcons[tipo], { fontSize: 'small' })}
                            </Box>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {label}
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {count}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Leyenda de Eventos
                  </Typography>
                  <Stack spacing={1.5}>
                    {Object.entries(tipoLabels).map(([tipo, label]) => (
                      <Box 
                        key={tipo}
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 2,
                          p: 1.5,
                          borderRadius: 1,
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                      >
                        <Box sx={{ 
                          width: 12, 
                          height: 12, 
                          borderRadius: '50%',
                          bgcolor: tipoColors[tipo]
                        }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                          {tipoIcons[tipo]}
                          <Typography variant="body2">
                            {label}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Próximos eventos
                    </Typography>
                    <Stack spacing={1}>
                      {Object.values(events)
                        .flat()
                        .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
                        .slice(0, 3)
                        .map(event => (
                          <Box 
                            key={event.id}
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              p: 1,
                              borderRadius: 1,
                              bgcolor: 'grey.50',
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: 'grey.200'
                              }
                            }}
                            onClick={() => handleEventClick(event, { stopPropagation: () => {} })}
                          >
                            <Box sx={{ 
                              width: 8, 
                              height: 8, 
                              borderRadius: '50%',
                              bgcolor: event.color || tipoColors[event.tipo]
                            }} />
                            <Typography variant="caption" sx={{ flexGrow: 1 }}>
                              {event.evento}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {dayjs(event.fecha).format('DD/MM')}
                            </Typography>
                          </Box>
                        ))}
                    </Stack>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Diálogo para agregar/editar evento */}
        <Dialog 
          open={openDialog} 
          onClose={() => setOpenDialog(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ 
            pb: 1, 
            bgcolor: formData.color || tipoColors[formData.tipo],
            color: 'white'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {tipoIcons[formData.tipo]}
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {editingEvent ? 'Editar Evento' : 'Nuevo Evento'}
              </Typography>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={2.5}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Fecha del evento"
                  value={dayjs(formData.fecha)}
                  onChange={(newValue) => {
                    setFormData({
                      ...formData,
                      fecha: newValue.format('YYYY-MM-DD')
                    });
                  }}
                  format="dddd, DD [de] MMMM [de] YYYY"
                  sx={{ width: '100%' }}
                />
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TimePicker
                      label="Hora de inicio"
                      value={dayjs(`2000-01-01 ${formData.hora_inicio}`)}
                      onChange={(newValue) => {
                        setFormData({
                          ...formData,
                          hora_inicio: newValue ? newValue.format('HH:mm') : '09:00'
                        });
                      }}
                      sx={{ width: '100%' }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TimePicker
                      label="Hora de fin"
                      value={dayjs(`2000-01-01 ${formData.hora_fin}`)}
                      onChange={(newValue) => {
                        setFormData({
                          ...formData,
                          hora_fin: newValue ? newValue.format('HH:mm') : '10:00'
                        });
                      }}
                      sx={{ width: '100%' }}
                    />
                  </Grid>
                </Grid>
              </LocalizationProvider>
              
              <TextField
                label="Título del evento"
                fullWidth
                value={formData.evento}
                onChange={(e) => setFormData({ ...formData, evento: e.target.value })}
                required
                size="medium"
                placeholder="Ej: Conferencia de Cardiología"
              />
              
              <FormControl fullWidth size="medium">
                <InputLabel>Tipo de Evento</InputLabel>
                <Select
                  value={formData.tipo}
                  label="Tipo de Evento"
                  onChange={(e) => {
                    const tipo = e.target.value;
                    setFormData({
                      ...formData,
                      tipo,
                      color: tipoColors[tipo]
                    });
                  }}
                >
                  {Object.entries(tipoLabels).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {tipoIcons[key]}
                        {label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                label="Descripción"
                fullWidth
                multiline
                rows={3}
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                size="medium"
                placeholder="Detalles adicionales sobre el evento..."
              />
              
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Color del evento
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {Object.entries(tipoColors).map(([tipo, color]) => (
                    <Tooltip key={tipo} title={tipoLabels[tipo]}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: color,
                          cursor: 'pointer',
                          border: formData.tipo === tipo ? '3px solid' : '2px solid',
                          borderColor: formData.tipo === tipo ? 'primary.main' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          '&:hover': {
                            transform: 'scale(1.1)'
                          },
                          transition: 'transform 0.2s'
                        }}
                        onClick={() => {
                          setFormData({
                            ...formData,
                            tipo,
                            color
                          });
                        }}
                      >
                        {tipoIcons[tipo]}
                      </Box>
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
            {editingEvent && (
              <Button 
                onClick={() => {
                  if (window.confirm('¿Está seguro de eliminar este evento?')) {
                    handleDeleteEvent(editingEvent.id, { stopPropagation: () => {} });
                    setOpenDialog(false);
                  }
                }}
                color="error"
                startIcon={<DeleteIcon />}
              >
                Eliminar
              </Button>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Button onClick={() => setOpenDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveEvent} 
              variant="contained" 
              color="primary"
              disabled={!formData.evento.trim()}
              sx={{ px: 4 }}
            >
              {editingEvent ? 'Actualizar' : 'Crear Evento'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default AgendaMedica;